"use client";

import React from "react";
import { Activity, Watch, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MeasuredDataComparison() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section ref={sectionRef} id="measureddatacomparison" data-nav="light" className="bg-background text-foreground py-20 md:py-28 transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground text-balance">
            Measured data rather than an algorithmic estimate
          </h2>
          <p className="mt-6 text-lg sm:text-xl text-muted-foreground leading-relaxed">
            Many watches and fitness platforms estimate VO₂ max using heart rate, pace, power, or other field data. A metabolic assessment instead measures the respiratory response directly during a controlled exercise protocol.
          </p>
        </div>

        {/* Comparison Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Wearables Card */}
          <Card className="border border-border/80 bg-card/60 shadow-xs relative overflow-hidden flex flex-col justify-between">
            <CardHeader className="space-y-3 pb-4">
              <div className="w-10 h-10 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center">
                <Watch className="size-5" aria-hidden="true" />
              </div>
              <CardTitle className="text-xl font-medium tracking-tight">
                Wearable & Field Estimates
              </CardTitle>
              <CardDescription className="text-base text-muted-foreground leading-normal">
                Useful for regular day-to-day tracking, trends, and continuous monitoring over weeks and months.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-2">
              <Separator />
              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-muted-foreground/70 shrink-0 mt-0.5" />
                  <span>Calculates metrics via proprietary algorithms and indirect heart rate signals</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-muted-foreground/70 shrink-0 mt-0.5" />
                  <span>Practical and accessible for everyday training logs and activity monitoring</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-muted-foreground/70 shrink-0 mt-0.5" />
                  <span>Subject to environmental variables, wrist movement, and algorithm assumptions</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Metabolic Assessment Card */}
          <Card className="border border-primary/20 bg-card shadow-sm relative overflow-hidden flex flex-col justify-between ring-1 ring-primary/10">
            <CardHeader className="space-y-3 pb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Activity className="size-5" aria-hidden="true" />
              </div>
              <CardTitle className="text-xl font-medium tracking-tight text-foreground">
                Clinical Metabolic Assessment
              </CardTitle>
              <CardDescription className="text-base text-muted-foreground leading-normal">
                Direct breath-by-breath gas exchange analysis conducted in a calibrated testing environment.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-2">
              <Separator />
              <div className="space-y-3 text-sm text-foreground/90">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Direct measurement of O₂ consumed and CO₂ expelled during incremental load</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Identifies precise ventilatory thresholds and substrate utilisation rates</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Provides individual physiological data to inform bespoke conditioning</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Clarifying Ground Statement */}
        <div className="p-6 md:p-8 rounded-xl bg-secondary/60 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-base sm:text-lg text-secondary-foreground/90 max-w-3xl leading-relaxed">
            Wearables can be useful for ongoing monitoring. Metabolic testing provides a different level of directly measured physiological information.
          </p>
          <Button asChild variant="default" className="shrink-0">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>View clinical services</span>
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}